import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { apiFetchJson } from '../../apiClient';
import './BuddyReads.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function BuddyReads({ factFictionMode }) {
  const { instance } = useMsal();
  const { t } = useTranslation();
  const [buddyReads, setBuddyReads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteUser, setInviteUser] = useState('');
  const [inviteBook, setInviteBook] = useState('');
  const [inviting, setInviting] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchBuddyReads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetchJson(instance, '/api/buddyreads');
      setBuddyReads(unwrap(data));
    } catch (err) {
      setError(err.message || 'Failed to load buddy reads');
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { fetchBuddyReads(); }, [fetchBuddyReads]);

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteUser.trim() || !inviteBook.trim()) return;
    setInviting(true);
    setError(null);
    try {
      await apiFetchJson(instance, '/api/buddyreads/invite', {
        method: 'POST',
        body: JSON.stringify({ partnerIdentifier: inviteUser.trim(), bookTitle: inviteBook.trim() }),
      });
      setInviteUser('');
      setInviteBook('');
      setShowInviteForm(false);
      fetchBuddyReads();
    } catch (err) {
      setError(err.message || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleAccept = async (id) => {
    setActionLoading(id);
    try {
      await apiFetchJson(instance, `/api/buddyreads/accept/${id}`, { method: 'POST' });
      fetchBuddyReads();
    } catch (err) {
      setError(err.message || 'Failed to accept invite');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (id) => {
    setActionLoading(id);
    try {
      await apiFetchJson(instance, `/api/buddyreads/${id}/complete`, { method: 'PUT' });
      fetchBuddyReads();
    } catch (err) {
      setError(err.message || 'Failed to mark as complete');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <div className="buddy-loading">{t('bookClub.buddyReads.loading')}</div>;

  const active = buddyReads.filter(b => b.status === 'Active' || b.status === 'active');
  const pending = buddyReads.filter(b => b.status === 'Pending' || b.status === 'pending');
  const completed = buddyReads.filter(b => b.status === 'Completed' || b.status === 'completed');

  return (
    <div className="buddy-container">
      <div className="buddy-header">
        <h3 className="buddy-title">{t('bookClub.buddyReads.title')}</h3>
        <button className="buddy-invite-toggle" onClick={() => setShowInviteForm(!showInviteForm)}>
          {showInviteForm ? t('bookClub.common.cancel') : '+ ' + t('bookClub.buddyReads.inviteBuddy')}
        </button>
      </div>

      {error && (
        <div className="buddy-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>{t('bookClub.common.dismiss')}</button>
        </div>
      )}

      {/* Invite Form */}
      {showInviteForm && (
        <form className="buddy-invite-form" onSubmit={handleInvite}>
          <input
            type="text"
            className="buddy-input"
            placeholder={t('bookClub.buddyReads.usernamePlaceholder')}
            value={inviteUser}
            onChange={e => setInviteUser(e.target.value)}
            required
          />
          <input
            type="text"
            className="buddy-input"
            placeholder={t('bookClub.buddyReads.bookTitlePlaceholder')}
            value={inviteBook}
            onChange={e => setInviteBook(e.target.value)}
            required
          />
          <button type="submit" className="buddy-send-btn" disabled={inviting}>
            {inviting ? t('bookClub.buddyReads.sending') : t('bookClub.buddyReads.sendInvite')}
          </button>
        </form>
      )}

      {/* Pending Invites */}
      {pending.length > 0 && (
        <div className="buddy-section">
          <h4 className="buddy-section-title">{t('bookClub.buddyReads.pendingInvites')}</h4>
          {pending.map(b => (
            <div key={b.id} className="buddy-card pending">
              <div className="buddy-avatars">
                <span className="buddy-avatar">👤</span>
                <span className="buddy-avatar-connector">↔</span>
                <span className="buddy-avatar">👤</span>
              </div>
              <div className="buddy-info">
                <span className="buddy-partner">{b.partnerName || b.partner || 'Buddy'}</span>
                <span className="buddy-book">{b.bookTitle || b.book || 'Book'}</span>
              </div>
              {b.canAccept !== false && (
                <button
                  className="buddy-accept-btn"
                  onClick={() => handleAccept(b.id)}
                  disabled={actionLoading === b.id}
                >
                  {actionLoading === b.id ? '…' : t('bookClub.buddyReads.accept')}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Active Buddy Reads */}
      <div className="buddy-section">
        <h4 className="buddy-section-title">{t('bookClub.buddyReads.active')}</h4>
        {active.length === 0 ? (
          <p className="buddy-empty">{t('bookClub.buddyReads.noActive')}</p>
        ) : (
          active.map(b => {
            const myProgress = b.myProgress ?? 0;
            const partnerProgress = b.partnerProgress ?? 0;
            return (
              <div key={b.id} className="buddy-card active">
                <div className="buddy-avatars">
                  <span className="buddy-avatar">👤</span>
                  <span className="buddy-avatar-connector">📖</span>
                  <span className="buddy-avatar">👤</span>
                </div>
                <div className="buddy-info">
                  <span className="buddy-partner">{b.partnerName || b.partner || 'Buddy'}</span>
                  <span className="buddy-book">{b.bookTitle || b.book || 'Book'}</span>
                  <div className="buddy-progress-comparison">
                    <div className="buddy-progress-row">
                      <span className="buddy-progress-label">{t('bookClub.buddyReads.you')}</span>
                      <div className="buddy-progress-bar-track">
                        <div className="buddy-progress-bar-fill mine" style={{ width: `${myProgress}%` }} />
                      </div>
                      <span className="buddy-progress-pct">{myProgress}%</span>
                    </div>
                    <div className="buddy-progress-row">
                      <span className="buddy-progress-label">{t('bookClub.buddyReads.buddy')}</span>
                      <div className="buddy-progress-bar-track">
                        <div className="buddy-progress-bar-fill partner" style={{ width: `${partnerProgress}%` }} />
                      </div>
                      <span className="buddy-progress-pct">{partnerProgress}%</span>
                    </div>
                  </div>
                </div>
                <button
                  className="buddy-complete-btn"
                  onClick={() => handleComplete(b.id)}
                  disabled={actionLoading === b.id}
                >
                  {actionLoading === b.id ? '…' : t('bookClub.buddyReads.done')}
                </button>
              </div>
            );
          })
        )}
      </div>

      {/* Completed History */}
      {completed.length > 0 && (
        <div className="buddy-section">
          <h4 className="buddy-section-title">{t('bookClub.buddyReads.completed')}</h4>
          {completed.map(b => (
            <div key={b.id} className="buddy-card completed">
              <div className="buddy-avatars">
                <span className="buddy-avatar">👤</span>
                <span className="buddy-avatar-connector">✅</span>
                <span className="buddy-avatar">👤</span>
              </div>
              <div className="buddy-info">
                <span className="buddy-partner">{b.partnerName || b.partner || 'Buddy'}</span>
                <span className="buddy-book">{b.bookTitle || b.book || 'Book'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default BuddyReads;
